<script>
import { format, parseISO, parse } from 'date-fns';
import { required, url } from '@vuelidate/validators';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import HelperTextPopup from 'dashboard/components/ui/HelperTextPopup.vue';
import { isValidURL } from '../helper/URLHelper';
import { getRegexp } from 'shared/helpers/Validators';
import { getCurrentUserLocale } from 'shared/helpers/timeHelper';
import {
  formatCurrency,
  formatCurrencyAmount,
  getCurrencyDecimals,
  getCurrencySymbol,
  hasValidCurrencyPrecision,
  normalizeCurrencyCode,
} from 'shared/helpers/currencyHelper';
import { useVuelidate } from '@vuelidate/core';

const DATE_FORMAT = 'yyyy-MM-dd';

export default {
  components: {
    MultiselectDropdown,
    HelperTextPopup,
  },
  props: {
    label: { type: String, required: true },
    description: { type: String, default: '' },
    values: { type: Array, default: () => [] },
    value: { type: [String, Number, Boolean], default: '' },
    showActions: { type: Boolean, default: false },
    attributeType: { type: String, default: 'text' },
    attributeRegex: {
      type: String,
      default: null,
    },
    regexCue: { type: String, default: null },
    attributeKey: { type: String, required: true },
    // Codigo ISO 4217 escolhido no campo do tipo dinheiro (#1243). Vazio em
    // campo criado antes da escolha existir -- `normalizeCurrencyCode` resolve
    // isso para o padrao, o mesmo que o backfill gravou no banco.
    attributeCurrency: { type: String, default: '' },
    contactId: { type: Number, default: null },
    // Desenho da #1243 para a sidebar de detalhes da conversa: nome + selo do
    // tipo em cima, valor dentro de uma caixa com a forma do proprio tipo
    // (R$, %, calendario, chevron) e as acoes sempre visiveis ao lado.
    // E prop, e nao CSS, porque o selo e a caixa sao MARCACAO nova -- e porque
    // este componente tambem monta a pagina de contatos, que segue no desenho
    // antigo.
    panel: { type: Boolean, default: false },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      isEditing: false,
      editedValue: null,
    };
  },
  computed: {
    displayValue() {
      if (this.isAttributeTypeDate) {
        if (!this.value) return '';
        try {
          // Se o valor for apenas YYYY-MM-DD, parseamos manualmente para evitar problemas de fuso horário
          if (
            typeof this.value === 'string' &&
            /^\d{4}-\d{2}-\d{2}$/.test(this.value)
          ) {
            const date = parse(this.value, 'yyyy-MM-dd', new Date());
            return format(date, 'dd/MM/yyyy');
          }
          // Caso contrário (ISO string com tempo), usamos parseISO e formatamos localmente
          return format(parseISO(this.value), 'dd/MM/yyyy');
        } catch (e) {
          return new Date(this.value).toLocaleDateString();
        }
      }
      if (this.isAttributeTypeCheckbox) {
        return this.value === 'false' ? false : this.value;
      }
      if (this.attributeType === 'percent' && this.value) {
        return `${this.value}%`;
      }
      // A MOEDA do campo da o simbolo e quantas casas existem; o locale da
      // conta da a grafia do numero. `150` guardado vira `R$ 150,00`.
      if (this.isAttributeTypeCurrency && this.hasValue) {
        return formatCurrency(
          this.value,
          this.currencyCode,
          this.accountLocale
        );
      }
      return this.value;
    },
    formattedValue() {
      if (this.isAttributeTypeDate) {
        if (!this.value) return format(new Date(), DATE_FORMAT);
        if (
          typeof this.value === 'string' &&
          /^\d{4}-\d{2}-\d{2}$/.test(this.value)
        ) {
          return this.value;
        }
        return format(parseISO(this.value), DATE_FORMAT);
      }
      return this.value;
    },
    listOptions() {
      return this.values.map((value, index) => ({
        id: index + 1,
        name: value,
      }));
    },
    selectedItem() {
      const id = this.values.indexOf(this.editedValue) + 1;
      return { id, name: this.editedValue };
    },
    isAttributeTypeCheckbox() {
      return this.attributeType === 'checkbox';
    },
    isAttributeTypeList() {
      return this.attributeType === 'list';
    },
    isAttributeTypeLink() {
      return this.attributeType === 'link';
    },
    isAttributeTypeDate() {
      return this.attributeType === 'date';
    },
    isAttributeTypeNumber() {
      return (
        this.attributeType === 'number' ||
        this.attributeType === 'currency' ||
        this.attributeType === 'percent'
      );
    },
    urlValue() {
      return isValidURL(this.value) ? this.value : '---';
    },
    hrefURL() {
      return isValidURL(this.value) ? this.value : '';
    },
    notAttributeTypeCheckboxAndList() {
      return !this.isAttributeTypeCheckbox && !this.isAttributeTypeList;
    },
    inputType() {
      if (this.isAttributeTypeLink) return 'url';
      if (this.isAttributeTypeNumber) return 'number';
      return this.attributeType;
    },
    // O locale da CONTA -- nao o do navegador. No dashboard o `$i18n.locale` e
    // justamente o da conta: o `App.vue` o carimba a partir de `accounts/get`.
    // A cascata do `getCurrentUserLocale` e a mesma que o datepicker usa, para
    // dinheiro e data nao divergirem quando uma das fontes falta.
    accountLocale() {
      return getCurrentUserLocale(this);
    },
    // A moeda e do CAMPO, nao do idioma da conta: quem escolheu foi quem criou
    // o campo, na tela de campos personalizados.
    currencyCode() {
      return normalizeCurrencyCode(this.attributeCurrency);
    },
    currencySymbol() {
      return getCurrencySymbol(this.currencyCode, this.accountLocale);
    },
    // Nem toda moeda tem centavo: o iene e o peso chileno tem zero casas, o
    // dinar kuwaitiano tem tres. O passo do input e a mensagem de erro saem
    // daqui para nao pedir centavo que a moeda nao tem.
    currencyDecimals() {
      return getCurrencyDecimals(this.currencyCode);
    },
    currencyStep() {
      if (!this.isAttributeTypeCurrency) return null;
      if (!this.currencyDecimals) return '1';

      return `0.${'0'.repeat(this.currencyDecimals - 1)}1`;
    },
    isAttributeTypeCurrency() {
      return this.attributeType === 'currency';
    },
    isAttributeTypePercent() {
      return this.attributeType === 'percent';
    },
    hasValue() {
      return (
        this.value !== '' && this.value !== null && this.value !== undefined
      );
    },
    // O selo reaproveita os rotulos da tela de campos personalizados: e o mesmo
    // vocabulario que a pessoa escolheu ao criar o campo.
    typeLabel() {
      const keys = {
        text: 'TEXT',
        number: 'NUMBER',
        currency: 'CURRENCY',
        percent: 'PERCENT',
        link: 'LINK',
        date: 'DATE',
        list: 'LIST',
        checkbox: 'CHECKBOX',
      };
      const key = keys[this.attributeType] || 'TEXT';
      return this.$t(`ATTRIBUTES_MGMT.ADD.FORM.TYPE.OPTIONS.${key}`);
    },
    // `displayValue` gruda o `%` no proprio texto; na caixa o sufixo e um
    // elemento a parte, encostado na direita, entao aqui ele sai.
    panelValue() {
      if (this.isAttributeTypePercent) return this.value;
      // A caixa do painel desenha o simbolo como elemento proprio, entao aqui
      // vai so o numero -- ver `formatCurrencyAmount`.
      if (this.isAttributeTypeCurrency) {
        return formatCurrencyAmount(
          this.value,
          this.currencyCode,
          this.accountLocale
        );
      }
      return this.displayValue;
    },
    panelPlaceholder() {
      if (this.isAttributeTypeDate) {
        return this.$t('CUSTOM_ATTRIBUTES.PANEL.SELECT_DATE');
      }
      return '---';
    },
    panelCheckboxLabel() {
      const on = this.displayValue && this.displayValue !== 'false';
      return this.$t(
        on
          ? 'CUSTOM_ATTRIBUTES.PANEL.CHECKBOX_TRUE'
          : 'CUSTOM_ATTRIBUTES.PANEL.CHECKBOX_FALSE'
      );
    },
    // A caixa do valor toma emprestada a forma dos seletores do painel --
    // mesmo fundo, mesma borda, mesmo raio -- para o campo personalizado nao
    // parecer um widget de outra tela no meio da coluna.
    panelBoxClasses() {
      return 'flex items-center flex-1 min-w-0 h-8 gap-1.5 px-2.5 text-xs border rounded-lg bg-surface-sunken border-line text-body';
    },
    // Mesmo cinza da caixa do valor ao lado: os dois sao a mesma peca, e o
    // botao branco recortava um buraco claro na linha.
    //
    // Com esse fundo o hover NAO pode ser de fundo: no tema claro
    // `surface-sunken` e `surface-hover` valem os dois `241 243 245`, entao um
    // `hover:bg-surface-hover` nao mudaria pixel nenhum. Quem responde ao mouse
    // e o contorno e o icone, como nos botoes dos cartoes de fluxo.
    panelActionClasses() {
      return 'flex items-center justify-center w-8 h-8 p-0 transition-colors duration-150 border rounded-lg shrink-0 border-line bg-surface-sunken dark:bg-surface-raised text-muted hover:border-brand hover:text-brand-text';
    },
    shouldShowErrorMessage() {
      return this.v$.editedValue.$error;
    },
    errorMessage() {
      if (this.v$.editedValue.url) {
        return this.$t('CUSTOM_ATTRIBUTES.VALIDATIONS.INVALID_URL');
      }
      // O vuelidate 2 nao expoe o resultado cru do validador: cada um vira um
      // objeto de estado, e quem diz se reprovou e o `$invalid`. Comparar com
      // `false` nunca casa, e a mensagem que aparecia era a do `required`.
      if (
        this.isAttributeTypeCurrency &&
        this.v$.editedValue.currencyPrecision.$invalid
      ) {
        return this.currencyDecimals
          ? this.$t('CUSTOM_ATTRIBUTES.VALIDATIONS.MAX_DECIMALS', {
              count: this.currencyDecimals,
            })
          : this.$t('CUSTOM_ATTRIBUTES.VALIDATIONS.NO_DECIMALS');
      }
      if (!this.v$.editedValue.regexValidation) {
        return this.regexCue
          ? this.regexCue
          : this.$t('CUSTOM_ATTRIBUTES.VALIDATIONS.INVALID_INPUT');
      }
      return this.$t('CUSTOM_ATTRIBUTES.VALIDATIONS.REQUIRED');
    },
  },
  watch: {
    value() {
      this.isEditing = false;
      this.editedValue = this.formattedValue;
    },
    contactId() {
      // Fix to solve validation not resetting when contactId changes in contact page
      this.v$.$reset();
    },
  },

  validations() {
    if (this.isAttributeTypeLink) {
      return {
        editedValue: { required, url },
      };
    }
    if (this.isAttributeTypeCurrency) {
      return {
        editedValue: {
          required,
          currencyPrecision: value =>
            hasValidCurrencyPrecision(value, this.currencyCode),
          regexValidation: value => {
            return !(
              this.attributeRegex && !getRegexp(this.attributeRegex).test(value)
            );
          },
        },
      };
    }
    return {
      editedValue: {
        required,
        regexValidation: value => {
          return !(
            this.attributeRegex && !getRegexp(this.attributeRegex).test(value)
          );
        },
      },
    };
  },
  mounted() {
    this.editedValue = this.formattedValue;
    this.$emitter.on(BUS_EVENTS.FOCUS_CUSTOM_ATTRIBUTE, this.onFocusAttribute);
  },
  destroyed() {
    this.$emitter.off(BUS_EVENTS.FOCUS_CUSTOM_ATTRIBUTE, this.onFocusAttribute);
  },
  methods: {
    onFocusAttribute(focusAttributeKey) {
      if (this.attributeKey === focusAttributeKey) {
        this.onEdit();
      }
    },
    focusInput() {
      if (this.$refs.inputfield) {
        this.$refs.inputfield.focus();
      }
    },
    onClickAway() {
      this.v$.$reset();
      this.isEditing = false;
    },
    onEdit() {
      this.isEditing = true;
      this.$nextTick(() => {
        this.focusInput();
      });
    },
    onUpdateListValue(value) {
      if (value) {
        this.editedValue = value.name;
        this.onUpdate();
      }
    },
    onUpdate() {
      const updatedValue =
        this.attributeType === 'date'
          ? parseISO(this.editedValue)
          : this.editedValue;
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }
      this.isEditing = false;
      this.$emit('update', this.attributeKey, updatedValue);
    },
    onDelete() {
      this.isEditing = false;
      this.v$.$reset();
      this.$emit('delete', this.attributeKey);
    },
    onCopy() {
      this.$emit('copy', this.value);
    },
  },
};
</script>

<template>
  <!-- Desenho da #1243 (canvas "Sidebar Detalhes"): nome do campo e selo do
       tipo em cima, valor dentro de uma caixa que veste a forma do tipo, e as
       acoes -- copiar, editar, excluir -- sempre visiveis. O antigo mostrava o
       valor como texto solto e escondia as acoes ate o mouse passar por cima,
       o que deixava a secao sem forma e as acoes indescobriveis.

       `custom-attribute` não pinta nada: é só o gancho para quem monta esta
       linha ajustar o respiro dela sem depender de `.px-4`, que é utilitário e
       pode aparecer em qualquer filho. -->
  <div v-if="panel" class="flex flex-col gap-1.5 py-2.5 custom-attribute">
    <div class="flex items-center gap-1.5">
      <span
        class="text-xs truncate"
        :class="
          v$.editedValue.$error
            ? 'text-red-400 dark:text-red-500'
            : 'text-muted'
        "
      >
        {{ label }}
      </span>
      <!-- O `HelperTextPopup` desenha o icone em `position: absolute` e a
           propria raiz dele e `w-[inherit]` -- ela CONTA com um pai que tenha
           largura. Sem este `span` o wrapper nasce com 0x0 e o "i" cai por
           cima do selo do tipo. Classe passada por fora nao resolve: o
           `w-[inherit]` dele ganha do `w-3.5` na ordem da folha. -->
      <span
        v-if="description"
        class="flex custom-attribute__help w-3.5 h-3.5 shrink-0"
      >
        <HelperTextPopup :message="description" />
      </span>
      <!-- O selo diz o tipo por extenso porque a forma da caixa so o insinua:
           "12 %" e "48210" sao a mesma coisa para quem nunca criou o campo. -->
      <span
        class="px-1.5 py-px leading-4 border rounded text-xxs shrink-0 text-muted border-line"
      >
        {{ typeLabel }}
      </span>
      <button
        v-if="showActions && hasValue"
        v-tooltip.left="$t('CUSTOM_ATTRIBUTES.ACTIONS.DELETE')"
        type="button"
        :aria-label="$t('CUSTOM_ATTRIBUTES.ACTIONS.DELETE')"
        class="flex items-center justify-center w-5 h-5 p-0 ml-auto transition-colors duration-150 rounded shrink-0 text-subtle rtl:ml-0 rtl:mr-auto hover:text-ruby-800"
        @click="onDelete"
      >
        <fluent-icon icon="delete" size="12" />
      </button>
    </div>

    <!-- Lista nao ganha lapis: o proprio seletor JA e a caixa da referencia --
         mesmo retangulo, mesmo chevron -- e um lapis ao lado abriria o mesmo
         menu que um clique na caixa abre. -->
    <MultiselectDropdown
      v-if="isAttributeTypeList"
      class="panel-attribute-select"
      :options="listOptions"
      :selected-item="selectedItem"
      :has-thumbnail="false"
      :multiselector-placeholder="$t('CUSTOM_ATTRIBUTES.PANEL.SELECT_VALUE')"
      :no-search-result="
        $t('CUSTOM_ATTRIBUTES.FORM.ATTRIBUTE_TYPE.LIST.NO_RESULT')
      "
      :input-placeholder="
        $t(
          'CUSTOM_ATTRIBUTES.FORM.ATTRIBUTE_TYPE.LIST.SEARCH_INPUT_PLACEHOLDER'
        )
      "
      @click="onUpdateListValue"
    />

    <div
      v-else-if="isEditing"
      v-on-clickaway="onClickAway"
      class="flex flex-col gap-1"
    >
      <div class="flex items-center gap-1">
        <input
          ref="inputfield"
          v-model="editedValue"
          :type="inputType"
          :step="currencyStep"
          class="flex-1 min-w-0 h-8 px-2.5 m-0 text-xs rounded-lg shadow-none reset-base bg-surface border border-line text-body focus:outline-none focus:border-brand"
          :class="{ '!border-red-400': v$.editedValue.$error }"
          @blur="v$.editedValue.$touch"
          @keyup.enter="onUpdate"
        />
        <button
          type="button"
          :class="panelActionClasses"
          :aria-label="$t('CUSTOM_ATTRIBUTES.FORM.UPDATE.SUCCESS')"
          @click="onUpdate"
        >
          <fluent-icon icon="checkmark" size="14" />
        </button>
      </div>
      <span
        v-if="shouldShowErrorMessage"
        class="block w-full font-normal text-red-400 text-xxs dark:text-red-500"
      >
        {{ errorMessage }}
      </span>
    </div>

    <div v-else class="flex items-center gap-1">
      <div :class="panelBoxClasses">
        <!-- A caixa de selecao tambem dispensa o lapis: o proprio quadradinho
             e o controle, e o `@change` ja grava. -->
        <template v-if="isAttributeTypeCheckbox">
          <input
            v-model="editedValue"
            type="checkbox"
            class="!m-0 shrink-0"
            @change="onUpdate"
          />
          <span class="truncate">{{ panelCheckboxLabel }}</span>
        </template>
        <template v-else>
          <fluent-icon
            v-if="isAttributeTypeDate"
            icon="calendar"
            size="12"
            class="shrink-0 text-subtle"
          />
          <span
            v-else-if="isAttributeTypeCurrency"
            class="shrink-0 text-subtle"
          >
            {{ currencySymbol }}
          </span>
          <a
            v-if="isAttributeTypeLink && hasValue"
            :href="hrefURL"
            target="_blank"
            rel="noopener noreferrer"
            class="truncate text-brand-text"
          >
            {{ urlValue }}
          </a>
          <span v-else class="truncate" :class="{ 'text-subtle': !hasValue }">
            {{ hasValue ? panelValue : panelPlaceholder }}
          </span>
          <fluent-icon
            v-if="isAttributeTypeLink && hasValue"
            icon="open"
            size="12"
            class="ml-auto shrink-0 text-subtle"
          />
          <span
            v-else-if="isAttributeTypePercent"
            class="ml-auto shrink-0 text-subtle"
          >
            %
          </span>
        </template>
      </div>
      <button
        v-if="showActions && hasValue && !isAttributeTypeCheckbox"
        v-tooltip.left="$t('CUSTOM_ATTRIBUTES.ACTIONS.COPY')"
        type="button"
        :aria-label="$t('CUSTOM_ATTRIBUTES.ACTIONS.COPY')"
        :class="panelActionClasses"
        @click="onCopy"
      >
        <fluent-icon icon="clipboard" size="14" />
      </button>
      <button
        v-if="showActions && !isAttributeTypeCheckbox"
        v-tooltip.left="$t('CUSTOM_ATTRIBUTES.ACTIONS.EDIT')"
        type="button"
        :aria-label="$t('CUSTOM_ATTRIBUTES.ACTIONS.EDIT')"
        :class="panelActionClasses"
        @click="onEdit"
      >
        <fluent-icon icon="edit" size="14" />
      </button>
    </div>
  </div>

  <div v-else class="px-4 py-3 custom-attribute">
    <div class="flex items-center mb-1">
      <h4 class="flex items-center w-full m-0 text-sm error">
        <div v-if="isAttributeTypeCheckbox" class="flex items-center">
          <input
            v-model="editedValue"
            class="!my-0 mr-2 ml-0"
            type="checkbox"
            @change="onUpdate"
          />
        </div>
        <div class="flex items-center justify-between w-full">
          <span
            class="w-full inline-flex gap-1.5 items-start font-medium whitespace-nowrap text-sm mb-0"
            :class="
              v$.editedValue.$error
                ? 'text-red-400 dark:text-red-500'
                : 'text-body'
            "
          >
            {{ label }}
            <HelperTextPopup
              v-if="description"
              :message="description"
              class="mt-0.5"
            />
          </span>
          <woot-button
            v-if="showActions && value"
            v-tooltip.left="$t('CUSTOM_ATTRIBUTES.ACTIONS.DELETE')"
            variant="link"
            size="medium"
            color-scheme="secondary"
            icon="delete"
            class-names="flex justify-end w-4"
            @click="onDelete"
          />
        </div>
      </h4>
    </div>
    <div v-if="notAttributeTypeCheckboxAndList">
      <div v-if="isEditing" v-on-clickaway="onClickAway">
        <div class="flex items-center w-full mb-2">
          <input
            ref="inputfield"
            v-model="editedValue"
            :type="inputType"
            class="!h-8 ltr:!rounded-r-none rtl:!rounded-l-none !mb-0 !text-sm focus:ring-2 focus:ring-primary-400 dark:focus:ring-primary-500 focus:border-primary-400 dark:focus:border-primary-500 outline-none transition-shadow duration-200"
            :class="{ error: v$.editedValue.$error }"
            @blur="v$.editedValue.$touch"
            @keyup.enter="onUpdate"
          />
          <div>
            <woot-button
              size="small"
              icon="checkmark"
              class="rounded-l-none rtl:rounded-r-none"
              @click="onUpdate"
            />
          </div>
        </div>
        <span
          v-if="shouldShowErrorMessage"
          class="block w-full -mt-px text-sm font-normal text-red-400 dark:text-red-500"
        >
          {{ errorMessage }}
        </span>
      </div>
      <div
        v-show="!isEditing"
        class="flex group"
        :class="{ 'is-editable': showActions }"
      >
        <a
          v-if="isAttributeTypeLink"
          :href="hrefURL"
          target="_blank"
          rel="noopener noreferrer"
          class="group-hover:bg-surface-hover inline-block rounded-sm mb-0 break-all py-0.5 px-1"
        >
          {{ urlValue }}
        </a>
        <p
          v-else
          class="group-hover:bg-surface-hover inline-block rounded-sm mb-0 break-all py-0.5 px-1"
        >
          {{ displayValue || '---' }}
        </p>
        <div class="flex max-w-[2rem] gap-1 ml-1 rtl:mr-1 rtl:ml-0">
          <woot-button
            v-if="showActions && value"
            v-tooltip="$t('CUSTOM_ATTRIBUTES.ACTIONS.COPY')"
            variant="link"
            size="small"
            color-scheme="secondary"
            icon="clipboard"
            class-names="hidden group-hover:flex !w-6 flex-shrink-0"
            @click="onCopy"
          />
          <woot-button
            v-if="showActions"
            v-tooltip.right="$t('CUSTOM_ATTRIBUTES.ACTIONS.EDIT')"
            variant="link"
            size="small"
            color-scheme="secondary"
            icon="edit"
            class-names="hidden group-hover:flex !w-6 flex-shrink-0"
            @click="onEdit"
          />
        </div>
      </div>
    </div>
    <div v-if="isAttributeTypeList">
      <MultiselectDropdown
        :options="listOptions"
        :selected-item="selectedItem"
        :has-thumbnail="false"
        :multiselector-placeholder="
          $t('CUSTOM_ATTRIBUTES.FORM.ATTRIBUTE_TYPE.LIST.PLACEHOLDER')
        "
        :no-search-result="
          $t('CUSTOM_ATTRIBUTES.FORM.ATTRIBUTE_TYPE.LIST.NO_RESULT')
        "
        :input-placeholder="
          $t(
            'CUSTOM_ATTRIBUTES.FORM.ATTRIBUTE_TYPE.LIST.SEARCH_INPUT_PLACEHOLDER'
          )
        "
        @click="onUpdateListValue"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
::v-deep {
  // O `HelperTextPopup` desenha a bolha em `position: absolute` com `w-fit`, e
  // `w-fit` mede contra o bloco que a contem -- aqui, os 14px do span do icone.
  // Resultado: "Se a assinatura do cliente e ativa ou nao" saia em seis linhas
  // de uma palavra. `w-max` faz a bolha medir o proprio texto; o teto de 12rem
  // e o que impede que ela estoure a largura da coluna.
  .custom-attribute__help {
    > .group > div {
      @apply w-max max-w-[12rem];
    }

    // O `mt-0.5` do componente nasceu para um uso em que o icone acompanha uma
    // linha de texto mais alta. Nesta linha ele desce o "i" 2px e o joga para
    // fora do centro, ao lado justamente do nome do campo.
    svg {
      @apply mt-0;
    }
  }

  // A lista usa o seletor do painel como caixa do valor. Sem isto ele chega
  // com a altura e a fonte do seletor de atendente -- 40px e 14px -- e fica
  // maior que as caixas dos campos vizinhos.
  .panel-attribute-select {
    @apply mb-0;

    .button {
      @apply h-8 min-h-0 px-2.5 rounded-lg bg-surface-sunken border-line;
    }

    h4 {
      @apply text-xs;
    }
  }

  .selector-wrap {
    @apply m-0 top-1;

    .selector-name {
      @apply ml-0;
    }
  }

  .name {
    @apply ml-0;
  }
}
</style>
